<template>
  <v-container class="dev-tools-container">
    <!-- Animierte Hintergrundelemente für zusätzlichen visuellen Effekt -->
    <div class="background-elements">
      <div class="ambient-light ambient-light-1"></div>
      <div class="ambient-light ambient-light-2"></div>
      <div class="ambient-light ambient-light-3"></div>
      <div class="grid-overlay"></div>
    </div>

    <v-card class="dev-tools-card">
      <div class="dev-tools-header">
        <div class="header-title">
          <v-icon color="primary" size="24" class="mr-2">mdi-tools</v-icon>
          Entwickler-Tools
        </div>
        <v-chip color="success" size="small" class="ml-2">{{ version }}</v-chip>
      </div>

      <v-tabs
        v-model="activeTab"
        color="success"
        align-tabs="center"
        class="tabs-container"
      >
        <v-tab value="dashboard" class="tab-item">
          <v-icon size="small" start>mdi-view-dashboard</v-icon>
          Dashboard
        </v-tab>
        <v-tab value="api" class="tab-item">
          <v-icon start>mdi-api</v-icon>
          API
        </v-tab>
        <v-tab value="database" class="tab-item">
          <v-icon start>mdi-database</v-icon>
          Datenbank
        </v-tab>
        <v-tab value="logs" class="tab-item">
          <v-icon start>mdi-text-box-search</v-icon>
          Logs
        </v-tab>
        <v-tab value="security" class="tab-item">
          <v-icon start>mdi-shield</v-icon>
          Sicherheit
        </v-tab>
        <v-tab value="components" class="tab-item">
          <v-icon start>mdi-puzzle</v-icon>
          Komponenten
        </v-tab>
        <v-tab value="config" class="tab-item">
          <v-icon start>mdi-cog</v-icon>
          Konfiguration
        </v-tab>
        <v-tab value="performance" class="tab-item">
          <v-icon start>mdi-chart-line</v-icon>
          Performance
        </v-tab>
      </v-tabs>

      <v-divider class="tabs-divider"></v-divider>

      <v-window v-model="activeTab" class="dev-tools-content">
        <!-- System Dashboard -->
        <v-window-item value="dashboard">
          <SystemDashboard
            :system-info="systemInfo"
            :loading="loading"
            @refresh="fetchSystemInfo"
          />
        </v-window-item>

        <!-- API Tools -->
        <v-window-item value="api">
          <ApiTools />
        </v-window-item>

        <!-- Database Tools -->
        <v-window-item value="database">
          <DatabaseTools />
        </v-window-item>

        <!-- Logging Tools -->
        <v-window-item value="logs">
          <LogViewer />
        </v-window-item>

        <!-- Security Tools -->
        <v-window-item value="security">
          <SecurityTools />
        </v-window-item>

        <!-- Component Explorer -->
        <v-window-item value="components">
          <ComponentExplorer />
        </v-window-item>

        <!-- Configuration Manager -->
        <v-window-item value="config">
          <ConfigManager />
        </v-window-item>

        <!-- Performance Tools -->
        <v-window-item value="performance">
          <PerformanceTools />
        </v-window-item>
      </v-window>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";
import { API_BASE_URL } from "@/config";
import { useToast } from "vue-toastification";
import VersionService from "@/services/VersionService";

// Komponenten importieren
import SystemDashboard from "@/components/devtools/SystemDashboard.vue";
import ApiTools from "@/components/devtools/ApiTools.vue";
import DatabaseTools from "@/components/devtools/DatabaseTools.vue";
import LogViewer from "@/components/devtools/LogViewer.vue";
import SecurityTools from "@/components/devtools/SecurityTools.vue";
import ComponentExplorer from "@/components/devtools/ComponentExplorer.vue";
import ConfigManager from "@/components/devtools/ConfigManager.vue";
import PerformanceTools from "@/components/devtools/PerformanceTools.vue";

// Router instanziieren
const router = useRouter();

// Tab-Steuerung
const activeTab = ref("dashboard");
const version = ref(VersionService.getVersion());
const toast = useToast();
const authStore = useAuthStore();
const loading = ref(false);
const systemInfo = ref({}); // systemInfo als reaktiver Wert definieren

// Bei Komponenten-Mount
onMounted(() => {
  // Prüfe Developer-Berechtigungen
  if (!authStore.user?.developer) {
    toast.error("Sie haben keine Berechtigung für diesen Bereich.");
    router.push("/dashboard");
    return;
  }

  // Systemdiagnose laden
  fetchSystemInfo();
});

// Systeminfo abrufen
const fetchSystemInfo = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}admin/system_info.php?action=get_info`, {
      withCredentials: true, // Wichtig! Sendet das JWT-Cookie mit
    });
    systemInfo.value = response.data;
  } catch (error) {
    console.error("Fehler beim Laden der Systeminformationen:", error);
    toast.error("Systeminformationen konnten nicht geladen werden");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.dev-tools-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.dev-tools-card {
  position: relative;
  z-index: 10;
  overflow: hidden;
  border-radius: 16px;
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.dev-tools-view {
  position: relative;
  min-height: calc(100vh - 64px);
}

/* ======== Hintergrundelemente ======== */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.15;
}

.ambient-light-1 {
  width: 700px;
  height: 700px;
  top: -200px;
  right: -100px;
  animation: pulse 15s infinite alternate;
}

.ambient-light-2 {
  width: 600px;
  height: 600px;
  bottom: -100px;
  left: -100px;
  animation: pulse 20s infinite alternate-reverse;
}

.ambient-light-3 {
  width: 500px;
  height: 500px;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 18s infinite alternate;
}

@keyframes pulse {
  0% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.dev-tools-header {
  color: white;
  padding: 8px 24px;
  border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  position: relative;
  display: flex;
  align-items: center;
}

.header-title {
  font-weight: 600;
  letter-spacing: 0.5px;
  font-size: 1.4rem;
  -webkit-background-clip: text;
  background-clip: text;
}

.tabs-container {
  background: rgba(35, 35, 45, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.tab-item {
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  font-weight: 500;
  opacity: 0.8;
  text-transform: none;
  border-radius: 4px 4px 0 0;
  margin: 0 2px;
}

.tab-item:hover {
  background: rgba(76, 175, 80, 0.1);
  opacity: 1;
}

.tab-item.v-tab--selected {
  background: rgba(76, 175, 80, 0.15);
  opacity: 1;
  font-weight: 600;
}

.tabs-divider {
  border-color: rgba(255, 255, 255, 0.06);
}

.dev-tools-content {
  padding: 24px;
  height: calc(100vh - 220px);
  overflow-y: auto;
}

.dev-tools-content::-webkit-scrollbar {
  width: 8px;
}

.dev-tools-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.dev-tools-content::-webkit-scrollbar-thumb {
  background: rgba(76, 175, 80, 0.3);
  border-radius: 4px;
}

.dev-tools-content::-webkit-scrollbar-thumb:hover {
  background: rgba(76, 175, 80, 0.5);
}

@media (max-width: 960px) {
  .dev-tools-view {
    padding: 12px;
  }

  .dev-tools-header {
    padding: 16px 20px;
  }

  .header-title {
    font-size: 1.2rem;
  }

  .dev-tools-content {
    padding: 16px;
    height: calc(100vh - 200px);
  }
}

@media (max-width: 600px) {
  .dev-tools-view {
    padding: 8px;
  }

  .dev-tools-content {
    padding: 12px;
  }

  .tab-item {
    min-width: auto;
  }
}
</style>
